// @ts-check
/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import Card from '@bottlebooks/gatsby-design-system/src/components/Card/Card';
import {
  Box as BBBox,
  Flex,
  Label,
  Link,
  ShoppingCartIcon,
  Text,
  VideoIcon,
} from '@bottlebooks/gatsby-theme-base/src';
import BaseExhibitorCard from '@bottlebooks/gatsby-theme-event/src/components/ExhibitorsList/ExhibitorCard';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { graphql as gatsbyGraphQL } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { LucideImages } from 'lucide-react';
import { ComponentPropsWithoutRef } from 'react';
import { jsx } from 'theme-ui';
import getCloudinaryImage from '../../../../getCloudinaryImage';
import { Box } from '@mantine/core';

export function StandardExhibitorCard({
  data,
  children,
  to,
  onClick,
  ...rest
}: ComponentPropsWithoutRef<typeof Card> & {
  data: SingleRegistration;
  children?: React.ReactNode;
  to?: string;
  onClick?: () => void;
}) {
  const exhibitor = afterQuery(data);
  return (
    <Card {...rest}>
      <BaseExhibitorCard.Link exhibitor={exhibitor} to={to} onClick={onClick}>
        <BaseExhibitorCard.Logo
          gap={0}
          exhibitor={exhibitor}
          sx={{ paddingTop: 2 }}
        >
          <CertificationLogos data={data} sx={{ marginTop: -1 }}>
            {children}
          </CertificationLogos>
        </BaseExhibitorCard.Logo>
        <BaseExhibitorCard.Main
          sx={{
            paddingTop: 2,
            backgroundColor: 'light',
            borderTop: 1,
            borderColor: 'border',
          }}
          exhibitor={exhibitor}
        >
          <Box c={exhibitor.stand?.room?.primaryColor || undefined} pb="xs">
            <Label>{exhibitor.stand?.name}</Label>
          </Box>
          <BaseExhibitorCard.Exhibitor exhibitor={exhibitor} />
        </BaseExhibitorCard.Main>
      </BaseExhibitorCard.Link>
      <BaseExhibitorCard.Bookmark exhibitor={exhibitor} />
    </Card>
  );
}

export function PremiumExhibitorCard({
  data,
  exhibitorPackageText = 'Premiumeintrag',
  elevation,
  to,
  onClick,
  className,
  ...rest
}: {
  data: SingleRegistration;
  elevation?: Parameters<typeof Card>[0]['elevation'];
  to?: string;
  onClick?: () => void;
  className?: string;
  exhibitorPackageText?: string;
}) {
  const exhibitor = afterQuery(data);
  const { i18n } = useLingui();
  return (
    <Card
      elevation={elevation}
      sx={{
        gridColumn: [null, null, 'span 2'],
        gridRow: 'span 2',
        gridAutoRows: '1fr 0 1fr',
        '.bb-hoverimg': {
          transition: 'transform',
          transformOrigin: 'top center',
        },
        ':hover .bb-hoverimg': { transform: 'scale(1.02)' },
      }}
      className={className}
    >
      <Link
        to={to}
        onClick={onClick}
        sx={{
          display: 'grid',
          overflow: 'hidden',
          margin: 3,
          marginBottom: -3,
          backgroundColor: '#f5f5f5',
        }}
      >
        {exhibitor.profile?.mainImage?.publicId ? (
          <GatsbyImage
            image={getCloudinaryImage({
              src: exhibitor.profile.mainImage.publicId,
              width: 700,
              height: 350,
              layout: 'constrained',
              loader: ({ width, src, quality }) =>
                `https://res.cloudinary.com/bottlebooks/image/upload/c_fill,g_faces:auto,f_auto,q_${quality},w_${width},h_${
                  width / 2
                }/${src}`,
              sizes: '(max-width: 555px) 100vw, 670px',
            })}
            alt=""
            className="bb-hoverimg"
          />
        ) : (
          <GatsbyImage
            image={getCloudinaryImage({
              src: 'bottlebooks/5a16e716c93586389fd8e9e6/Wein_Burgenland-VieVinum-Test-eventLogo-VieVinum-Logo-3327.png',
              width: 350,
              height: 350,
              layout: 'constrained',
              loader: ({ width, src, quality }) =>
                `https://res.cloudinary.com/bottlebooks/image/upload/c_pad,g_center,f_auto,o_10,q_${quality},w_${width},h_${width}/${src}`,
              sizes: '350px',
            })}
            alt=""
            sx={{ marginX: 'auto' }}
            className="bb-hoverimg"
          />
        )}
      </Link>
      <Flex aria-hidden sx={{ zIndex: 'elevated', paddingX: 3 }}>
        <BBBox
          sx={{
            borderBottom: '16px solid',
            borderBottomColor: 'background',
            borderRight: '16px solid',
            borderRightColor: 'transparent',
            flex: 1,
          }}
        >
          <Text variant="smallest" sx={{ color: 'lightText', paddingTop: 0.5 }}>
            {exhibitorPackageText}
          </Text>
        </BBBox>
        <BBBox
          sx={{
            borderBottom: '16px solid',
            borderBottomColor: 'background',
            borderLeft: '16px solid',
            borderLeftColor: 'transparent',
            flex: 1,
          }}
        ></BBBox>
      </Flex>

      <StandardExhibitorCard
        data={data}
        to={to}
        onClick={onClick}
        sx={{ marginTop: 3 }}
        {...rest}
        elevation="none"
      >
        {exhibitor.offeringTypes?.includes('onlineShop') && (
          <ShoppingCartIcon size="xsmall" title={t`Online shop`} />
        )}
        {exhibitor.offeringTypes?.includes('videos') && (
          <VideoIcon size="xsmall" title={i18n._(t`Videos`)} />
        )}
        {exhibitor.offeringTypes?.includes('imageGallery') && (
          <LucideImages
          // title={i18n._(t`Image gallery`)}
          />
        )}
      </StandardExhibitorCard>
    </Card>
  );
}

function CertificationLogos({
  data,
  children,
  ...rest
}: ComponentPropsWithoutRef<typeof Flex> & {
  data: SingleRegistration;
  children: React.ReactNode;
}) {
  const exhibitor = afterQuery(data);
  return (
    <Flex
      gap={3}
      justify="flex-end"
      align="center"
      sx={{ color: 'primary' }}
      {...rest}
    >
      {exhibitor.zertifizierungslogos?.map(({ key, value }) => (
        <GatsbyImage
          image={getCloudinaryImage({
            layout: 'fixed',
            width: 32,
            height: 32,
            src: `sites/vievinum-2024/logos/${key}`,
            loader: ({ src, width, quality }) =>
              `https://res.cloudinary.com/bottlebooks/image/upload/c_pad,g_center,f_auto,q_${quality},w_${width},h_${width}/${src}`,
          })}
          key={key}
          alt={value}
          title={value}
        />
      ))}
      {children}
    </Flex>
  );
}

export function PavilionCard({
  data,
  ...rest
}: ComponentPropsWithoutRef<typeof PremiumExhibitorCard>) {
  return (
    <PremiumExhibitorCard
      data={data}
      exhibitorPackageText="Premium-Pavillon"
      {...rest}
      sx={{
        gridColumn: ['1/-1', null, '1/-1'],
        gridAutoRows: ['1fr 0 1fr', null, null, '2fr 0 1fr'],
        '.bb-hoverimg > div[aria-hidden]': { display: 'none' },
      }}
    />
  );
}

export const fragment = gatsbyGraphQL/* GraphQL */ `
  fragment VieVinumExhibitorCard_Registration on Bottlebooks_SingleRegistration {
    collection {
      logo {
        publicId
      }
    }
    registrationId
    profile {
      offeringTypes
      mainImage {
        publicId
      }
    }
    stand {
      name
      number
      room {
        primaryColor
        contrastColor
      }
    }
    zertifizierungslogos: customFieldValue(key: "zertifizierungslogos") {
      ... on Bottlebooks_MultiSelectFieldValue {
        values {
          key
          value
        }
      }
    }
  }
`;

interface SingleRegistration {
  registrationId: string;
  stand?: null | {
    name?: null | string;
    number?: string;
    room?: null | {
      name: string;
      primaryColor: string;
      contrastColor: string;
    };
  };
  collection: {
    logo: {
      publicId: string;
    } | null;
  } | null;
  profile: {
    offeringTypes: string[] | null;
    mainImage: {
      publicId: string;
    } | null;
    logo: {
      publicId: string | null;
    } | null;
  } | null;
  zertifizierungslogos: {
    values:
      | {
          key: string;
          value: string;
        }[]
      | null;
  };
}

function afterQuery(exhibitor: SingleRegistration) {
  return {
    ...exhibitor,
    profile: {
      ...exhibitor.profile,
      logo: exhibitor.profile?.logo?.publicId
        ? exhibitor.profile?.logo
        : exhibitor.collection?.logo,
    },
    offeringTypes: exhibitor?.profile?.offeringTypes,
    zertifizierungslogos: exhibitor?.zertifizierungslogos?.values?.filter(
      (logo) => logo.key && logo.key !== 'notSelected'
    ),
  };
}
